import { useCallback } from 'react';

const formatComponentDisplayName = (name: string): string => {
  if (!name) return '';
  let displayName = name.replace(
    /Halfswitch Specific Circuit Receptacle/gi,
    'Halfswitch Receptacle'
  );
  if (/^wall mounted .*receptacle$/i.test(displayName)) {
    displayName = displayName.replace(/^wall mounted\s+/i, '');
  }
  return displayName.trim();
};

export interface UseDrawAnnotationsParams {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  debugTileGridRef: React.RefObject<{
    gridSize: number;
    tileWidth: number;
    tileHeight: number;
    overlap: number;
    hiResWidth: number;
    hiResHeight: number;
  } | null>;
  annotations: any[];
  pageNumber: number;
  selectedAnnotation: string | null;
  isDrawing: boolean;
  currentAnnotation: any;
  dragStart: { x: number; y: number } | null;
  resizeHandle: {
    annotationId: string;
    handle: 'nw' | 'ne' | 'sw' | 'se';
    startX: number;
    startY: number;
    origRect: { startX: number; startY: number; width: number; height: number };
  } | null;
  currentColor: string;
  thickness: number;
  highlightedComponentType: string | null;
  viewAllMode: boolean;
  animatingAnnotations: Set<string>;
  selectionRect: { startX: number; startY: number; endX: number; endY: number } | null;
  isSelecting: boolean;
  selectedAnnotations: Set<string>;
  snapshotSelection: { startX: number; startY: number; endX: number; endY: number } | null;
  showSnapshotActions: boolean;
  calibrationLine: { startX: number; startY: number; endX: number; endY: number; percentage: number } | null;
  isCalibrating: boolean;
  isGridSnapActive: boolean;
  calibrationData: Record<number, any>;
  pipingPreview: { startX: number; startY: number; endX: number; endY: number } | null;
  pipingSnapTarget: { annotationId: string } | null;
  currentTool: string;
  isElementDetectionMode: boolean;
  hoveredPDFElement: any;
  autocountSelection: { startX: number; startY: number; endX: number; endY: number } | null;
  autocountMode: string | null;
  keyExtractionSelection: { startX: number; startY: number; endX: number; endY: number } | null;
  keyExtractionMode: boolean;
  lightingScheduleSelection: { startX: number; startY: number; endX: number; endY: number } | null;
  lightingScheduleMode: boolean;
  showDebugTileGrid: boolean;
  wireRunExtendMode: { annotationId: string; endpoint: 'start' | 'end' } | null;
  wireRunExtendPreviewPoint: { x: number; y: number } | null;
  hoveredAnnotation: string | null;
  hoveredAnnotationPoint: { x: number; y: number } | null;
  rescaleAnnotation: (ann: any, newWidth: number, newHeight: number) => any;
  getColorForAnnotation: (annotation: any) => string;
  getAnimationScale: (annotationId: string) => number;
  calculatePipeMeasurement: (canvasDistance: number, pxDx?: number, pxDy?: number) => { value: number; unit: string } | null;
}

export function useDrawAnnotations(params: UseDrawAnnotationsParams) {
  const {
    canvasRef,
    debugTileGridRef,
    annotations,
    pageNumber,
    selectedAnnotation,
    isDrawing,
    currentAnnotation,
    dragStart,
    resizeHandle,
    currentColor,
    thickness,
    highlightedComponentType,
    viewAllMode,
    animatingAnnotations,
    selectionRect,
    isSelecting,
    selectedAnnotations,
    snapshotSelection,
    showSnapshotActions,
    calibrationLine,
    isCalibrating,
    isGridSnapActive,
    calibrationData,
    pipingPreview,
    pipingSnapTarget,
    currentTool,
    isElementDetectionMode,
    hoveredPDFElement,
    autocountSelection,
    autocountMode,
    keyExtractionSelection,
    keyExtractionMode,
    lightingScheduleSelection,
    lightingScheduleMode,
    showDebugTileGrid,
    wireRunExtendMode,
    wireRunExtendPreviewPoint,
    hoveredAnnotation,
    hoveredAnnotationPoint,
    rescaleAnnotation,
    getColorForAnnotation,
    getAnimationScale,
    calculatePipeMeasurement,
  } = params;

  const hasActiveFilter = highlightedComponentType !== null && highlightedComponentType !== '__ALL__';

  const drawAnnotations = useCallback(
    (skipUI = false) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // In element detection mode, skip drawing regular annotations
      if (isElementDetectionMode) {
        // Skip all regular annotation drawing - jump to PDF element highlight
      } else {
        const pageAnnotations = annotations
          .filter((a: any) => a.page === pageNumber)
          .map((a: any) => rescaleAnnotation(a, canvas.width, canvas.height));

        // Interaction focus mode: suppress expensive overlay work and hide unrelated annotations
        // during draw/drag/resize interactions for smoother pointer updates.
        const isDrawingPlacementBox =
          isDrawing &&
          currentTool === 'select' &&
          currentAnnotation?.type === 'rectangle';
        const isDraggingExistingAnnotation =
          (currentTool === 'select' || currentTool === 'drag') &&
          Boolean(dragStart) &&
          !isDrawing &&
          !isSelecting &&
          !resizeHandle;
        const isResizingExistingAnnotation =
          currentTool === 'select' && Boolean(resizeHandle);
        const isInteractionFocusMode =
          isDrawingPlacementBox || isDraggingExistingAnnotation || isResizingExistingAnnotation;

        let annotationsForRender = pageAnnotations;
        if (isDrawingPlacementBox) {
          annotationsForRender = [];
        } else if (isResizingExistingAnnotation && resizeHandle?.annotationId) {
          annotationsForRender = pageAnnotations.filter(
            (annotation: any) => annotation.id === resizeHandle.annotationId
          );
        } else if (isDraggingExistingAnnotation) {
          const selectedIds = new Set(selectedAnnotations);
          if (selectedAnnotation) selectedIds.add(selectedAnnotation);
          annotationsForRender = selectedIds.size
            ? pageAnnotations.filter((annotation: any) => selectedIds.has(annotation.id))
            : [];
        }

        const effectiveViewAllMode = viewAllMode && !isInteractionFocusMode;
        const isBeaconViewMode = Boolean(effectiveViewAllMode);

        if (!skipUI && isBeaconViewMode && annotationsForRender.length > 0) {
          ctx.save();
          ctx.fillStyle = 'rgba(6, 12, 20, 0.56)';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.globalCompositeOperation = 'destination-out';

          annotationsForRender.forEach((annotation: any) => {
            if (annotation.type === 'rectangle') {
              const x = Math.min(annotation.startX, annotation.startX + (annotation.width || 0));
              const y = Math.min(annotation.startY, annotation.startY + (annotation.height || 0));
              const width = Math.abs(annotation.width || 0);
              const height = Math.abs(annotation.height || 0);
              if (width > 0 && height > 0) ctx.fillRect(x, y, width, height);
              return;
            }
            if (annotation.type === 'circle') {
              const centerX = annotation.x ?? (annotation.startX + (annotation.width || 0) / 2);
              const centerY = annotation.y ?? (annotation.startY + (annotation.height || 0) / 2);
              const radiusFromNumber = annotation.number
                ? String(annotation.number).length === 1
                  ? 24
                  : String(annotation.number).length === 2
                    ? 28
                    : 32
                : null;
              const radius =
                radiusFromNumber ||
                annotation.radius ||
                Math.max(8, Math.min(Math.abs(annotation.width || 0), Math.abs(annotation.height || 0)) / 2);
              ctx.beginPath();
              ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
              ctx.fill();
              return;
            }
            if (annotation.type === 'hand-square-footage') {
              const x = annotation.startX;
              const y = annotation.startY;
              const width = (annotation.endX || annotation.startX) - annotation.startX;
              const height = (annotation.endY || annotation.startY) - annotation.startY;
              if (Math.abs(width) > 0 && Math.abs(height) > 0) ctx.fillRect(x, y, width, height);
            }
          });

          ctx.restore();

          ctx.save();
          annotationsForRender.forEach((annotation: any) => {
            const isHighlighted = highlightedComponentType === annotation.component_type;
            const interiorLightAlpha = hasActiveFilter
              ? (isHighlighted ? 0.2 : 0.1)
              : 0.14;
            const annotationColor = getColorForAnnotation(annotation);
            const interiorColorMatch = annotationColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
            if (interiorColorMatch) {
              ctx.fillStyle = `rgba(${interiorColorMatch[1]}, ${interiorColorMatch[2]}, ${interiorColorMatch[3]}, ${interiorLightAlpha})`;
            } else if (annotationColor.startsWith('#')) {
              const hex = annotationColor.replace('#', '');
              const r = parseInt(hex.substring(0, 2), 16);
              const g = parseInt(hex.substring(2, 4), 16);
              const b = parseInt(hex.substring(4, 6), 16);
              ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${interiorLightAlpha})`;
            } else {
              ctx.fillStyle = `rgba(255, 255, 255, ${interiorLightAlpha})`;
            }

            if (annotation.type === 'rectangle') {
              const x = Math.min(annotation.startX, annotation.startX + (annotation.width || 0));
              const y = Math.min(annotation.startY, annotation.startY + (annotation.height || 0));
              const width = Math.abs(annotation.width || 0);
              const height = Math.abs(annotation.height || 0);
              if (width > 0 && height > 0) ctx.fillRect(x, y, width, height);
              return;
            }
            if (annotation.type === 'circle') {
              const centerX = annotation.x ?? (annotation.startX + (annotation.width || 0) / 2);
              const centerY = annotation.y ?? (annotation.startY + (annotation.height || 0) / 2);
              const radiusFromNumber = annotation.number
                ? String(annotation.number).length === 1
                  ? 24
                  : String(annotation.number).length === 2
                    ? 28
                    : 32
                : null;
              const radius =
                radiusFromNumber ||
                annotation.radius ||
                Math.max(8, Math.min(Math.abs(annotation.width || 0), Math.abs(annotation.height || 0)) / 2);
              ctx.beginPath();
              ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
              ctx.fill();
              return;
            }
            if (annotation.type === 'hand-square-footage') {
              const x = annotation.startX;
              const y = annotation.startY;
              const width = (annotation.endX || annotation.startX) - annotation.startX;
              const height = (annotation.endY || annotation.startY) - annotation.startY;
              if (Math.abs(width) > 0 && Math.abs(height) > 0) ctx.fillRect(x, y, width, height);
            }
          });
          ctx.restore();
        }

        annotationsForRender.forEach((annotation: any) => {
          if (!highlightedComponentType && !effectiveViewAllMode && annotation.type !== 'wire-run') return;

          const isHighlighted = hasActiveFilter && highlightedComponentType === annotation.component_type;
          const isAnimating = animatingAnnotations.has(annotation.id);
          const isMultiSelected = selectedAnnotations.has(annotation.id);

          let isGhosted = false;
          if (!highlightedComponentType) {
            isGhosted = annotation.type !== 'wire-run';
          } else if (hasActiveFilter) {
            if (annotation.type === 'wire-run') {
              isGhosted = true;
            } else if (annotation.type === 'piping') {
              const startAnn = annotations.find((a: any) => a.id === annotation.startAnnotationId);
              const endAnn = annotations.find((a: any) => a.id === annotation.endAnnotationId);
              const shouldShowPipe =
                startAnn?.component_type === highlightedComponentType ||
                endAnn?.component_type === highlightedComponentType;
              if (!shouldShowPipe) {
                if (effectiveViewAllMode) isGhosted = true;
                else return;
              }
            } else if (annotation.component_type !== highlightedComponentType) {
              if (effectiveViewAllMode) isGhosted = true;
              else return;
            }
          }

          if (isGhosted) {
            ctx.save();
            ctx.globalAlpha = isBeaconViewMode ? 0.85 : 0.5;
          }

          const shouldShowOnlyNumber = isHighlighted && !isAnimating;

          if (!shouldShowOnlyNumber || annotation.type === 'rectangle') {
            const isSelected = isMultiSelected || annotation.id === selectedAnnotation;

            if (isSelected) {
              ctx.save();
              ctx.strokeStyle = '#0066ff';
              ctx.lineWidth = 6;
              ctx.setLineDash([12, 6]);
              ctx.lineCap = 'round';
              ctx.lineJoin = 'round';
              ctx.shadowColor = '#0066ff';
              ctx.shadowBlur = 8;
              ctx.shadowOffsetX = 0;
              ctx.shadowOffsetY = 0;

              if (annotation.type === 'rectangle') {
                ctx.strokeRect(
                  annotation.startX - 8,
                  annotation.startY - 8,
                  (annotation.width || 0) + 16,
                  (annotation.height || 0) + 16
                );
              } else if (annotation.type === 'circle') {
                let centerX, centerY, radius;
                if (annotation.x !== undefined && annotation.y !== undefined) {
                  centerX = annotation.x;
                  centerY = annotation.y;
                  radius = (annotation.radius || 5) + 8;
                } else {
                  centerX = annotation.startX + (annotation.width || 0) / 2;
                  centerY = annotation.startY + (annotation.height || 0) / 2;
                  if (annotation.number) {
                    const text = String(annotation.number);
                    radius = (text.length === 1 ? 12 : text.length === 2 ? 14 : 16) + 6;
                  } else {
                    radius = Math.min(annotation.width || 0, annotation.height || 0) / 2 + 8;
                  }
                }
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
                ctx.stroke();
              } else if (annotation.type === 'line') {
                const size = 14;
                ctx.strokeRect(annotation.startX - size / 2, annotation.startY - size / 2, size, size);
                ctx.strokeRect(
                  (annotation.endX || annotation.startX) - size / 2,
                  (annotation.endY || annotation.startY) - size / 2,
                  size,
                  size
                );
              }
              ctx.restore();
            }

            const annotationColor = getColorForAnnotation(annotation);
            ctx.strokeStyle = annotationColor;
            ctx.lineWidth = annotation.thickness;

            const colorMatch = annotationColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
            if (colorMatch) {
              ctx.fillStyle = `rgba(${colorMatch[1]}, ${colorMatch[2]}, ${colorMatch[3]}, 0.16)`;
            } else {
              ctx.fillStyle = annotationColor + '33';
            }

            switch (annotation.type) {
              case 'rectangle': {
                const rectColor = getColorForAnnotation(annotation);
                let rectFillColor = rectColor;
                const rectColorMatch = rectColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
                if (rectColorMatch) {
                  rectFillColor = `rgba(${rectColorMatch[1]}, ${rectColorMatch[2]}, ${rectColorMatch[3]}, 0.18)`;
                } else if (rectColor.startsWith('#')) {
                  const hex = rectColor.replace('#', '');
                  const r = parseInt(hex.substring(0, 2), 16);
                  const g = parseInt(hex.substring(2, 4), 16);
                  const b = parseInt(hex.substring(4, 6), 16);
                  rectFillColor = `rgba(${r}, ${g}, ${b}, 0.18)`;
                }

                if (isBeaconViewMode) {
                  const x = Math.min(annotation.startX, annotation.startX + (annotation.width || 0));
                  const y = Math.min(annotation.startY, annotation.startY + (annotation.height || 0));
                  const width = Math.abs(annotation.width || 0);
                  const height = Math.abs(annotation.height || 0);
                  const beaconBorderColor = rectColor;
                  ctx.save();
                  ctx.strokeStyle = beaconBorderColor;
                  ctx.lineWidth = isHighlighted ? 4.5 : 2.25;
                  ctx.shadowColor = beaconBorderColor;
                  ctx.shadowBlur = isHighlighted || isSelected ? 28 : 10;
                  ctx.strokeRect(x, y, width, height);
                  ctx.restore();
                } else {
                  ctx.fillStyle = rectFillColor;
                  ctx.fillRect(
                    annotation.startX,
                    annotation.startY,
                    annotation.width || 0,
                    annotation.height || 0
                  );
                  ctx.strokeStyle = rectColor;
                  ctx.lineWidth = 3;
                  ctx.strokeRect(
                    annotation.startX,
                    annotation.startY,
                    annotation.width || 0,
                    annotation.height || 0
                  );
                }

                const isThisSelected =
                  annotation.id === selectedAnnotation || selectedAnnotations.has(annotation.id);
                if (isThisSelected && currentTool === 'select') {
                  const handleSize = 10;
                  const handles = [
                    { x: annotation.startX, y: annotation.startY },
                    { x: annotation.startX + (annotation.width || 0), y: annotation.startY },
                    { x: annotation.startX, y: annotation.startY + (annotation.height || 0) },
                    {
                      x: annotation.startX + (annotation.width || 0),
                      y: annotation.startY + (annotation.height || 0),
                    },
                  ];
                  handles.forEach((handle: { x: number; y: number }) => {
                    ctx.fillStyle = 'white';
                    ctx.fillRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize);
                    ctx.strokeStyle = '#0066ff';
                    ctx.lineWidth = 2;
                    ctx.strokeRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize);
                  });
                }
                break;
              }

              case 'circle': {
                let centerX, centerY;
                if (annotation.x !== undefined && annotation.y !== undefined) {
                  centerX = annotation.x;
                  centerY = annotation.y;
                } else {
                  centerX = annotation.startX + (annotation.width || 0) / 2;
                  centerY = annotation.startY + (annotation.height || 0) / 2;
                }

                const animScale = isAnimating ? getAnimationScale(annotation.id) : 1;

                if (annotation.number) {
                  const text = String(annotation.number);
                  const radius = text.length === 1 ? 24 : text.length === 2 ? 28 : 32;
                  const animatedRadius = radius * animScale;
                  const circleColor = getColorForAnnotation(annotation);

                  let fillColor = circleColor;
                  const cMatch = circleColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
                  if (cMatch) {
                    fillColor = `rgba(${cMatch[1]}, ${cMatch[2]}, ${cMatch[3]}, 0.4)`;
                  } else if (circleColor.startsWith('#')) {
                    const hex = circleColor.replace('#', '');
                    const r = parseInt(hex.substring(0, 2), 16);
                    const g = parseInt(hex.substring(2, 4), 16);
                    const b = parseInt(hex.substring(4, 6), 16);
                    fillColor = `rgba(${r}, ${g}, ${b}, 0.4)`;
                  }

                  ctx.beginPath();
                  ctx.arc(centerX, centerY, animatedRadius, 0, 2 * Math.PI);
                  if (isBeaconViewMode) {
                    const beaconBorderColor = circleColor;
                    ctx.strokeStyle = beaconBorderColor;
                    ctx.lineWidth = isHighlighted ? 4 : 2;
                    ctx.shadowColor = beaconBorderColor;
                    ctx.shadowBlur = isHighlighted || isSelected ? 24 : 9;
                    ctx.stroke();
                    ctx.shadowBlur = 0;
                  } else {
                    ctx.fillStyle = fillColor;
                    ctx.fill();
                    ctx.strokeStyle = circleColor;
                    ctx.lineWidth = 2;
                    ctx.stroke();
                  }
                  ctx.strokeStyle = circleColor;
                  ctx.lineWidth = annotation.thickness || 2;
                } else {
                  const circleColor = getColorForAnnotation(annotation);
                  const animatedDotRadius = 5 * animScale;

                  let fillColor = circleColor;
                  const cMatch = circleColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
                  if (cMatch) {
                    fillColor = `rgba(${cMatch[1]}, ${cMatch[2]}, ${cMatch[3]}, 0.4)`;
                  } else if (circleColor.startsWith('#')) {
                    const hex = circleColor.replace('#', '');
                    const r = parseInt(hex.substring(0, 2), 16);
                    const g = parseInt(hex.substring(2, 4), 16);
                    const b = parseInt(hex.substring(4, 6), 16);
                    fillColor = `rgba(${r}, ${g}, ${b}, 0.4)`;
                  }

                  ctx.beginPath();
                  ctx.arc(centerX, centerY, animatedDotRadius, 0, 2 * Math.PI);
                  if (isBeaconViewMode) {
                    const beaconBorderColor = circleColor;
                    ctx.strokeStyle = beaconBorderColor;
                    ctx.lineWidth = isHighlighted ? 3 : 1.5;
                    ctx.shadowColor = beaconBorderColor;
                    ctx.shadowBlur = isHighlighted || isSelected ? 20 : 8;
                    ctx.stroke();
                    ctx.shadowBlur = 0;
                  } else {
                    ctx.fillStyle = fillColor;
                    ctx.fill();
                    ctx.strokeStyle = circleColor;
                    ctx.lineWidth = 1;
                    ctx.stroke();
                  }
                }
                break;
              }

              case 'line':
                ctx.beginPath();
                ctx.moveTo(annotation.startX, annotation.startY);
                ctx.lineTo(annotation.endX || annotation.startX, annotation.endY || annotation.startY);
                ctx.stroke();
                break;

              case 'pen':
                if (annotation.points && annotation.points.length > 0) {
                  ctx.beginPath();
                  ctx.moveTo(annotation.points[0].x, annotation.points[0].y);
                  annotation.points.forEach((point: { x: number; y: number }) => {
                    ctx.lineTo(point.x, point.y);
                  });
                  ctx.stroke();
                }
                break;

              case 'text':
                if (annotation.text) {
                  ctx.font = '16px Arial';
                  ctx.fillText(annotation.text, annotation.startX, annotation.startY);
                }
                break;

              case 'piping':
                if (isSelected) {
                  ctx.save();
                  ctx.strokeStyle = '#0066ff';
                  ctx.lineWidth = 8;
                  ctx.setLineDash([12, 6]);
                  ctx.lineCap = 'round';
                  ctx.shadowColor = '#0066ff';
                  ctx.shadowBlur = 8;
                  ctx.beginPath();
                  ctx.moveTo(annotation.startX, annotation.startY);
                  ctx.lineTo(annotation.endX || annotation.startX, annotation.endY || annotation.startY);
                  ctx.stroke();
                  ctx.setLineDash([]);
                  ctx.restore();
                }
                ctx.beginPath();
                ctx.moveTo(annotation.startX, annotation.startY);
                ctx.lineTo(annotation.endX || annotation.startX, annotation.endY || annotation.startY);
                ctx.stroke();
                if (annotation.measurement && annotation.measurementUnit) {
                  const midX = (annotation.startX + (annotation.endX || annotation.startX)) / 2;
                  const midY = (annotation.startY + (annotation.endY || annotation.startY)) / 2;
                  const label = `${annotation.measurement}${annotation.measurementUnit}`;
                  ctx.font = 'bold 24px Arial';
                  const textMetrics = ctx.measureText(label);
                  const padding = 10;
                  ctx.fillStyle = 'white';
                  ctx.fillRect(
                    midX - textMetrics.width / 2 - padding,
                    midY - 16,
                    textMetrics.width + padding * 2,
                    32
                  );
                  ctx.fillStyle = annotation.color;
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'middle';
                  ctx.fillText(label, midX, midY);
                }
                ctx.fillStyle = annotation.color;
                ctx.beginPath();
                ctx.arc(annotation.startX, annotation.startY, 6, 0, Math.PI * 2);
                ctx.fill();
                ctx.beginPath();
                ctx.arc(
                  annotation.endX || annotation.startX,
                  annotation.endY || annotation.startY,
                  6,
                  0,
                  Math.PI * 2
                );
                ctx.fill();
                break;

              case 'wire-run':
                if (!annotation.points || annotation.points.length < 2) break;
                const wireColor = annotation.wireTypeColor || annotation.color;

                if (isSelected) {
                  ctx.save();
                  ctx.strokeStyle = '#0066ff';
                  ctx.lineWidth = 8;
                  ctx.setLineDash([12, 6]);
                  ctx.lineCap = 'round';
                  ctx.lineJoin = 'round';
                  ctx.shadowColor = '#0066ff';
                  ctx.shadowBlur = 8;
                  ctx.beginPath();
                  ctx.moveTo(annotation.points[0].x, annotation.points[0].y);
                  for (let i = 1; i < annotation.points.length; i++) {
                    ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
                  }
                  ctx.stroke();
                  ctx.setLineDash([]);
                  ctx.restore();
                }

                ctx.strokeStyle = wireColor;
                ctx.lineWidth = 3;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.globalAlpha = 1;
                ctx.beginPath();
                ctx.moveTo(annotation.points[0].x, annotation.points[0].y);
                for (let i = 1; i < annotation.points.length; i++) {
                  ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
                }
                ctx.stroke();

                annotation.points.forEach((point: { x: number; y: number }, index: number) => {
                  ctx.beginPath();
                  ctx.arc(point.x, point.y, index === 0 ? 5 : 4, 0, Math.PI * 2);
                  ctx.fillStyle = wireColor;
                  ctx.fill();
                });

                if (annotation.totalLength !== undefined) {
                  const midIndex = Math.floor(annotation.points.length / 2);
                  const labelPoint = annotation.points[midIndex];
                  ctx.font = 'bold 16px Arial';
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'middle';
                  const unit = annotation.measurementUnit || 'ft';
                  const text = `${annotation.totalLength.toFixed(1)} ${unit}`;
                  const textWidth = ctx.measureText(text).width + 20;
                  ctx.fillStyle = wireColor;
                  ctx.beginPath();
                  ctx.roundRect(labelPoint.x - textWidth / 2, labelPoint.y - 14, textWidth, 28, 4);
                  ctx.fill();
                  ctx.strokeStyle = '#fff';
                  ctx.lineWidth = 2;
                  ctx.beginPath();
                  ctx.roundRect(labelPoint.x - textWidth / 2, labelPoint.y - 14, textWidth, 28, 4);
                  ctx.stroke();
                  ctx.fillStyle = '#fff';
                  ctx.fillText(text, labelPoint.x, labelPoint.y);
                }

                if (annotation.wireTypeName) {
                  const startPoint = annotation.points[0];
                  ctx.font = 'bold 14px Arial';
                  ctx.textAlign = 'left';
                  ctx.textBaseline = 'bottom';
                  const nameText = annotation.wireTypeName;
                  const nameWidth = ctx.measureText(nameText).width + 12;
                  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
                  ctx.beginPath();
                  ctx.roundRect(startPoint.x - 2, startPoint.y - 28, nameWidth, 22, 3);
                  ctx.fill();
                  ctx.fillStyle = wireColor;
                  ctx.fillText(nameText, startPoint.x + 4, startPoint.y - 10);
                }
                break;

              case 'hand-square-footage': {
                const sqftX = annotation.startX;
                const sqftY = annotation.startY;
                const sqftW = (annotation.endX || annotation.startX) - annotation.startX;
                const sqftH = (annotation.endY || annotation.startY) - annotation.startY;
                const sqftColor = annotation.color || '#9333EA';

                if (isSelected) {
                  ctx.save();
                  ctx.strokeStyle = '#0066ff';
                  ctx.lineWidth = 4;
                  ctx.setLineDash([8, 4]);
                  ctx.shadowColor = '#0066ff';
                  ctx.shadowBlur = 6;
                  ctx.strokeRect(sqftX, sqftY, sqftW, sqftH);
                  ctx.setLineDash([]);
                  ctx.restore();
                }

                ctx.globalAlpha = 0.06;
                ctx.fillStyle = sqftColor;
                ctx.fillRect(sqftX, sqftY, sqftW, sqftH);

                const pageCal = calibrationData[annotation.page];
                if (pageCal && canvas) {
                  let pxPerUnit = 0;
                  if (
                    pageCal.mode === 'preset' &&
                    pageCal.realUnitsPerPdfPoint &&
                    pageCal.pageWidthPt &&
                    pageCal.pageHeightPt
                  ) {
                    const pxPerPtX = canvas.width / pageCal.pageWidthPt;
                    const pxPerPtY = canvas.height / pageCal.pageHeightPt;
                    const pxPerPt = (pxPerPtX + pxPerPtY) / 2;
                    const ptPerUnit = 1 / pageCal.realUnitsPerPdfPoint;
                    pxPerUnit = ptPerUnit * pxPerPt;
                  } else if (pageCal.canvasPercentage && pageCal.realWorldValue) {
                    pxPerUnit = (pageCal.canvasPercentage / 100) * canvas.width / pageCal.realWorldValue;
                  }
                  if (pxPerUnit > 5) {
                    ctx.save();
                    ctx.beginPath();
                    ctx.rect(sqftX, sqftY, sqftW, sqftH);
                    ctx.clip();
                    ctx.globalAlpha = 0.18;
                    ctx.strokeStyle = sqftColor;
                    ctx.lineWidth = 0.5;
                    ctx.setLineDash([3, 3]);
                    const gridLeft = sqftW > 0 ? sqftX : sqftX + sqftW;
                    const gridTop = sqftH > 0 ? sqftY : sqftY + sqftH;
                    const gridRight = sqftW > 0 ? sqftX + sqftW : sqftX;
                    const gridBottom = sqftH > 0 ? sqftY + sqftH : sqftY;
                    for (let gx = gridLeft + pxPerUnit; gx < gridRight; gx += pxPerUnit) {
                      ctx.beginPath();
                      ctx.moveTo(gx, gridTop);
                      ctx.lineTo(gx, gridBottom);
                      ctx.stroke();
                    }
                    for (let gy = gridTop + pxPerUnit; gy < gridBottom; gy += pxPerUnit) {
                      ctx.beginPath();
                      ctx.moveTo(gridLeft, gy);
                      ctx.lineTo(gridRight, gy);
                      ctx.stroke();
                    }
                    ctx.setLineDash([]);
                    ctx.restore();
                  }
                }

                ctx.globalAlpha = 0.6;
                ctx.strokeStyle = sqftColor;
                ctx.lineWidth = 1.5;
                ctx.strokeRect(sqftX, sqftY, sqftW, sqftH);

                if (annotation.totalArea !== undefined) {
                  const cx = sqftX + sqftW / 2;
                  const cy = sqftY + sqftH / 2;
                  const unit = annotation.areaUnit || 'ft';
                  const areaText = `${annotation.totalArea.toFixed(1)} sq ${unit}`;
                  ctx.font = 'bold 14px Arial';
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'middle';
                  const tw = ctx.measureText(areaText).width + 16;
                  ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
                  ctx.beginPath();
                  ctx.roundRect(cx - tw / 2, cy - 12, tw, 24, 4);
                  ctx.fill();
                  ctx.fillStyle = '#fff';
                  ctx.fillText(areaText, cx, cy);
                }
                break;
              }
            }
          }

          if ((shouldShowOnlyNumber || isAnimating) && annotation.number && annotation.type === 'circle') {
            let centerX, centerY;
            if (annotation.x !== undefined && annotation.y !== undefined) {
              centerX = annotation.x;
              centerY = annotation.y;
            } else if (annotation.startX !== undefined && annotation.startY !== undefined) {
              centerX = annotation.startX + (annotation.width || 0) / 2;
              centerY = annotation.startY + (annotation.height || 0) / 2;
            } else {
              return;
            }

            let scale = 1;
            if (isAnimating) {
              const animationProgress = (Date.now() % 800) / 800;
              if (animationProgress < 0.5) {
                scale = 1 + animationProgress * 2 * 4;
              } else {
                scale = 5 - (animationProgress - 0.5) * 2 * 2;
              }
            } else if (shouldShowOnlyNumber) {
              scale = 3;
            }

            const text = String(annotation.number);
            const baseRadius = text.length === 1 ? 12 : text.length === 2 ? 14 : 16;
            const radius = baseRadius * scale;
            const enlargedColor = getColorForAnnotation(annotation);

            let fillColor = enlargedColor;
            const colorMatch = enlargedColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
            if (colorMatch) {
              fillColor = `rgba(${colorMatch[1]}, ${colorMatch[2]}, ${colorMatch[3]}, 0.4)`;
            } else if (enlargedColor.startsWith('#')) {
              const hex = enlargedColor.replace('#', '');
              const r = parseInt(hex.substring(0, 2), 16);
              const g = parseInt(hex.substring(2, 4), 16);
              const b = parseInt(hex.substring(4, 6), 16);
              fillColor = `rgba(${r}, ${g}, ${b}, 0.4)`;
            }

            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
            if (isBeaconViewMode) {
              ctx.strokeStyle = enlargedColor;
              ctx.lineWidth = 4 * scale;
              ctx.shadowColor = enlargedColor;
              ctx.shadowBlur = 18;
              ctx.stroke();
              ctx.shadowBlur = 0;
            } else {
              ctx.fillStyle = fillColor;
              ctx.fill();
              ctx.strokeStyle = enlargedColor;
              ctx.lineWidth = 3 * scale;
              ctx.stroke();
            }
          }

          if (isGhosted) ctx.restore();
        });

        if (!skipUI && wireRunExtendMode && wireRunExtendPreviewPoint) {
          const targetWireRun = pageAnnotations.find(
            (a: any) =>
              a.id === wireRunExtendMode.annotationId &&
              a.type === 'wire-run' &&
              !!a.points &&
              a.points.length >= 2
          );
          if (targetWireRun?.points && targetWireRun.points.length >= 2) {
            const endpointIndex = wireRunExtendMode.endpoint === 'start' ? 0 : targetWireRun.points.length - 1;
            const endpoint = targetWireRun.points[endpointIndex];
            const previewPoint = wireRunExtendPreviewPoint;
            const wireColor = targetWireRun.wireTypeColor || targetWireRun.color || '#3b82f6';
            ctx.save();
            ctx.strokeStyle = wireColor;
            ctx.fillStyle = wireColor;
            ctx.lineWidth = 3;
            ctx.setLineDash([8, 5]);
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.beginPath();
            ctx.moveTo(endpoint.x, endpoint.y);
            ctx.lineTo(previewPoint.x, previewPoint.y);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.arc(endpoint.x, endpoint.y, 7, 0, Math.PI * 2);
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 2;
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(previewPoint.x, previewPoint.y, 5, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1.5;
            ctx.stroke();
            ctx.restore();
          }
        }

        if (!skipUI && isBeaconViewMode && hoveredAnnotationPoint && hoveredAnnotation) {
          const hovered = annotationsForRender.find((a: any) => a.id === hoveredAnnotation);
          if (hovered && hovered.component_type && hovered.component_type !== highlightedComponentType) {
            const label = formatComponentDisplayName(hovered.component_type);
            ctx.save();
            ctx.font = '600 12px Inter, system-ui, sans-serif';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            const textWidth = ctx.measureText(label).width;
            const boxWidth = textWidth + 16;
            const boxHeight = 24;
            const tooltipX = Math.min(hoveredAnnotationPoint.x + 12, canvas.width - boxWidth - 8);
            const tooltipY = Math.max(12, hoveredAnnotationPoint.y - 26);
            ctx.fillStyle = 'rgba(12, 18, 28, 0.9)';
            ctx.beginPath();
            ctx.roundRect(tooltipX, tooltipY, boxWidth, boxHeight, 6);
            ctx.fill();
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
            ctx.fillText(label, tooltipX + 8, tooltipY + boxHeight / 2);
            ctx.restore();
          }
        }

        if (isDrawing && currentAnnotation) {
          ctx.strokeStyle = currentColor;
          ctx.fillStyle = currentColor;
          ctx.lineWidth = thickness;

          switch (currentAnnotation.type) {
            case 'rectangle':
              ctx.strokeRect(
                currentAnnotation.startX || 0,
                currentAnnotation.startY || 0,
                currentAnnotation.width || 0,
                currentAnnotation.height || 0
              );
              break;
            case 'circle': {
              const centerX = (currentAnnotation.startX || 0) + (currentAnnotation.width || 0) / 2;
              const centerY = (currentAnnotation.startY || 0) + (currentAnnotation.height || 0) / 2;
              const componentType = currentAnnotation.component_type || 'annotation';
              const existingCount = annotations.filter(
                (a: any) => a.component_type === componentType && a.page === pageNumber
              ).length;
              const nextNumber = existingCount + 1;
              const text = String(nextNumber);
              const radius = text.length === 1 ? 24 : text.length === 2 ? 28 : 32;
              let fillColor = currentColor;
              const cMatch = currentColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
              if (cMatch) {
                fillColor = `rgba(${cMatch[1]}, ${cMatch[2]}, ${cMatch[3]}, 0.4)`;
              } else if (currentColor.startsWith('#')) {
                const hex = currentColor.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                fillColor = `rgba(${r}, ${g}, ${b}, 0.4)`;
              }
              ctx.beginPath();
              ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
              ctx.fillStyle = fillColor;
              ctx.fill();
              ctx.strokeStyle = currentColor;
              ctx.lineWidth = 2;
              ctx.stroke();
              ctx.font = `bold ${text.length === 1 ? '14' : '12'}px Arial`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
              ctx.lineWidth = 3;
              ctx.lineJoin = 'round';
              ctx.miterLimit = 2;
              ctx.strokeText(text, centerX, centerY);
              ctx.fillStyle = 'white';
              ctx.fillText(text, centerX, centerY);
              break;
            }
            case 'line':
              ctx.beginPath();
              ctx.moveTo(currentAnnotation.startX || 0, currentAnnotation.startY || 0);
              ctx.lineTo(currentAnnotation.endX || 0, currentAnnotation.endY || 0);
              ctx.stroke();
              break;
            case 'pen':
              if (currentAnnotation.points && currentAnnotation.points.length > 0) {
                ctx.beginPath();
                ctx.moveTo(currentAnnotation.points[0].x, currentAnnotation.points[0].y);
                currentAnnotation.points.forEach((point: { x: number; y: number }) => {
                  ctx.lineTo(point.x, point.y);
                });
                ctx.stroke();
              }
              break;
          }
        }

        if (selectionRect && isSelecting) {
          ctx.strokeStyle = 'rgba(0, 123, 255, 0.8)';
          ctx.lineWidth = 1;
          ctx.setLineDash([5, 5]);
          const width = selectionRect.endX - selectionRect.startX;
          const height = selectionRect.endY - selectionRect.startY;
          ctx.strokeRect(selectionRect.startX, selectionRect.startY, width, height);
          ctx.fillStyle = 'rgba(0, 123, 255, 0.1)';
          ctx.fillRect(selectionRect.startX, selectionRect.startY, width, height);
          ctx.setLineDash([]);
        }

        if (!skipUI && autocountSelection && autocountMode === 'area-selection') {
          ctx.strokeStyle = 'rgba(59, 130, 246, 0.9)';
          ctx.lineWidth = 2;
          ctx.setLineDash([8, 4]);
          const x = Math.min(autocountSelection.startX, autocountSelection.endX);
          const y = Math.min(autocountSelection.startY, autocountSelection.endY);
          const width = Math.abs(autocountSelection.endX - autocountSelection.startX);
          const height = Math.abs(autocountSelection.endY - autocountSelection.startY);
          ctx.strokeRect(x, y, width, height);
          ctx.fillStyle = 'rgba(59, 130, 246, 0.15)';
          ctx.fillRect(x, y, width, height);
          ctx.setLineDash([]);
        }

        if (!skipUI && keyExtractionSelection && keyExtractionMode) {
          ctx.strokeStyle = 'rgba(139, 92, 246, 0.9)';
          ctx.lineWidth = 2;
          ctx.setLineDash([8, 4]);
          const x = Math.min(keyExtractionSelection.startX, keyExtractionSelection.endX);
          const y = Math.min(keyExtractionSelection.startY, keyExtractionSelection.endY);
          const width = Math.abs(keyExtractionSelection.endX - keyExtractionSelection.startX);
          const height = Math.abs(keyExtractionSelection.endY - keyExtractionSelection.startY);
          ctx.strokeRect(x, y, width, height);
          ctx.fillStyle = 'rgba(139, 92, 246, 0.15)';
          ctx.fillRect(x, y, width, height);
          ctx.setLineDash([]);
        }

        if (!skipUI && lightingScheduleSelection && lightingScheduleMode) {
          ctx.strokeStyle = 'rgba(245, 158, 11, 0.9)';
          ctx.lineWidth = 2;
          ctx.setLineDash([8, 4]);
          const x = Math.min(lightingScheduleSelection.startX, lightingScheduleSelection.endX);
          const y = Math.min(lightingScheduleSelection.startY, lightingScheduleSelection.endY);
          const width = Math.abs(lightingScheduleSelection.endX - lightingScheduleSelection.startX);
          const height = Math.abs(lightingScheduleSelection.endY - lightingScheduleSelection.startY);
          ctx.strokeRect(x, y, width, height);
          ctx.fillStyle = 'rgba(245, 158, 11, 0.15)';
          ctx.fillRect(x, y, width, height);
          ctx.setLineDash([]);
        }

        if (!skipUI && (snapshotSelection || showSnapshotActions)) {
          if (snapshotSelection) {
            const width = Math.abs(snapshotSelection.endX - snapshotSelection.startX);
            const height = Math.abs(snapshotSelection.endY - snapshotSelection.startY);
            const x = Math.min(snapshotSelection.startX, snapshotSelection.endX);
            const y = Math.min(snapshotSelection.startY, snapshotSelection.endY);
            const MIN_SIZE = 300;
            const willUpscale = width < MIN_SIZE || height < MIN_SIZE;
            const fillColor = willUpscale ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)';
            ctx.fillStyle = fillColor;
            ctx.fillRect(x, y, width, height);
            ctx.strokeStyle = willUpscale ? 'rgba(245, 158, 11, 1)' : 'rgba(16, 185, 129, 1)';
            ctx.lineWidth = 3;
            ctx.setLineDash([]);
            ctx.strokeRect(x, y, width, height);
            const handleSize = 8;
            ctx.fillStyle = willUpscale ? 'rgba(245, 158, 11, 1)' : 'rgba(16, 185, 129, 1)';
            ctx.fillRect(x - handleSize / 2, y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(x + width - handleSize / 2, y - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(x - handleSize / 2, y + height - handleSize / 2, handleSize, handleSize);
            ctx.fillRect(x + width - handleSize / 2, y + height - handleSize / 2, handleSize, handleSize);
            if (width > 10 && height > 10) {
              const textBg = willUpscale ? 'rgba(245, 158, 11, 0.9)' : 'rgba(0, 123, 255, 0.9)';
              const text = willUpscale
                ? `${Math.round(width)}×${Math.round(height)}px → ${Math.max(MIN_SIZE, Math.round(width * Math.max(MIN_SIZE / width, MIN_SIZE / height)))}×${Math.max(MIN_SIZE, Math.round(height * Math.max(MIN_SIZE / width, MIN_SIZE / height)))}px`
                : `${Math.round(width)}×${Math.round(height)}px`;
              ctx.font = '12px Inter, system-ui, sans-serif';
              const textWidth = ctx.measureText(text).width;
              ctx.fillStyle = textBg;
              ctx.fillRect(x, y - 22, textWidth + 8, 20);
              ctx.fillStyle = 'white';
              ctx.fillText(text, x + 4, y - 8);
            }
            ctx.setLineDash([]);
          }
        }

        const currentPageCalibration = calibrationData[pageNumber];
        if (
          currentPageCalibration?.referenceLine &&
          currentPageCalibration?.showReferenceLine !== false
        ) {
          ctx.save();
          const refLine = currentPageCalibration.referenceLine;
          ctx.strokeStyle = '#fbbf24';
          ctx.lineWidth = 3;
          ctx.setLineDash([10, 5]);
          ctx.beginPath();
          ctx.moveTo(refLine.startX, refLine.startY);
          ctx.lineTo(refLine.endX, refLine.endY);
          ctx.stroke();
          ctx.fillStyle = '#fbbf24';
          ctx.beginPath();
          ctx.arc(refLine.startX, refLine.startY, 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(refLine.endX, refLine.endY, 4, 0, Math.PI * 2);
          ctx.fill();
          const midX = (refLine.startX + refLine.endX) / 2;
          const midY = (refLine.startY + refLine.endY) / 2;
          const label = `${currentPageCalibration.realWorldValue}${currentPageCalibration.unit}`;
          ctx.font = 'bold 14px Arial';
          ctx.fillStyle = '#fbbf24';
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 3;
          ctx.strokeText(label, midX, midY - 15);
          ctx.fillText(label, midX, midY - 15);
          ctx.setLineDash([]);
          ctx.restore();
        }

        if (isCalibrating && isGridSnapActive && calibrationLine) {
          ctx.save();
          const guideLength = Math.max(canvas.width, canvas.height);
          for (let angle = 0; angle < 360; angle += 15) {
            const radians = angle * (Math.PI / 180);
            const endX = calibrationLine.startX + guideLength * Math.cos(radians);
            const endY = calibrationLine.startY + guideLength * Math.sin(radians);
            const isMajorAngle = angle % 90 === 0;
            ctx.strokeStyle = isMajorAngle ? 'rgba(37, 99, 235, 0.3)' : 'rgba(37, 99, 235, 0.15)';
            ctx.lineWidth = isMajorAngle ? 1.5 : 1;
            ctx.setLineDash(isMajorAngle ? [8, 4] : [4, 4]);
            ctx.beginPath();
            ctx.moveTo(calibrationLine.startX, calibrationLine.startY);
            ctx.lineTo(endX, endY);
            ctx.stroke();
          }
          ctx.setLineDash([]);
          ctx.restore();
        }

        if (pipingSnapTarget && currentTool === 'piping') {
          const snapAnnotation = annotations.find((a: any) => a.id === pipingSnapTarget.annotationId);
          if (snapAnnotation) {
            ctx.save();
            const pulseScale = 1 + Math.sin(Date.now() / 150) * 0.15;
            ctx.strokeStyle = 'rgba(34, 197, 94, 0.8)';
            ctx.lineWidth = 4 * pulseScale;
            ctx.setLineDash([8, 4]);
            ctx.shadowColor = 'rgba(34, 197, 94, 0.6)';
            ctx.shadowBlur = 15 * pulseScale;
            if (snapAnnotation.type === 'circle') {
              const centerX =
                snapAnnotation.x !== undefined
                  ? snapAnnotation.x
                  : snapAnnotation.startX + (snapAnnotation.width || 0) / 2;
              const centerY =
                snapAnnotation.y !== undefined
                  ? snapAnnotation.y
                  : snapAnnotation.startY + (snapAnnotation.height || 0) / 2;
              const radius =
                snapAnnotation.radius ||
                Math.max(snapAnnotation.width || 0, snapAnnotation.height || 0) / 2;
              ctx.beginPath();
              ctx.arc(centerX, centerY, radius + 15 * pulseScale, 0, Math.PI * 2);
              ctx.stroke();
            } else if (snapAnnotation.type === 'rectangle') {
              ctx.strokeRect(
                snapAnnotation.startX - 10 * pulseScale,
                snapAnnotation.startY - 10 * pulseScale,
                (snapAnnotation.width || 0) + 20 * pulseScale,
                (snapAnnotation.height || 0) + 20 * pulseScale
              );
            }
            ctx.setLineDash([]);
            ctx.restore();
          }
        }

        if (pipingPreview && currentTool === 'piping') {
          ctx.save();
          ctx.strokeStyle = pipingSnapTarget ? 'rgba(34, 197, 94, 0.8)' : 'rgba(220, 38, 38, 0.6)';
          ctx.lineWidth = pipingSnapTarget ? 5 : 4;
          ctx.setLineDash([10, 5]);
          ctx.beginPath();
          ctx.moveTo(pipingPreview.startX, pipingPreview.startY);
          ctx.lineTo(pipingPreview.endX, pipingPreview.endY);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = 'rgb(220, 38, 38)';
          ctx.beginPath();
          ctx.arc(pipingPreview.startX, pipingPreview.startY, 6, 0, Math.PI * 2);
          ctx.fill();
          if (pipingSnapTarget) {
            ctx.fillStyle = 'rgba(34, 197, 94, 1)';
            ctx.beginPath();
            ctx.arc(pipingPreview.endX, pipingPreview.endY, 8, 0, Math.PI * 2);
            ctx.fill();
          }
          const dx = pipingPreview.endX - pipingPreview.startX;
          const dy = pipingPreview.endY - pipingPreview.startY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const measurement = calculatePipeMeasurement(distance);
          if (measurement) {
            const midX = (pipingPreview.startX + pipingPreview.endX) / 2;
            const midY = (pipingPreview.startY + pipingPreview.endY) / 2;
            const label = `${measurement.value}${measurement.unit}`;
            ctx.font = 'bold 24px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = pipingSnapTarget ? 'rgba(34, 197, 94, 1)' : 'rgb(220, 38, 38)';
            ctx.strokeStyle = 'white';
            ctx.lineWidth = 4;
            ctx.strokeText(label, midX, midY);
            ctx.fillText(label, midX, midY);
          }
          ctx.restore();
        }

        if (calibrationLine) {
          ctx.save();
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 3;
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.moveTo(calibrationLine.startX, calibrationLine.startY);
          ctx.lineTo(calibrationLine.endX, calibrationLine.endY);
          ctx.stroke();
          ctx.fillStyle = '#f59e0b';
          ctx.beginPath();
          ctx.arc(calibrationLine.startX, calibrationLine.startY, 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(calibrationLine.endX, calibrationLine.endY, 5, 0, Math.PI * 2);
          ctx.fill();
          const dx = calibrationLine.endX - calibrationLine.startX;
          const dy = calibrationLine.endY - calibrationLine.startY;
          const angle = Math.atan2(dy, dx) * (180 / Math.PI);
          const displayAngle = Math.round(angle);
          const midX = (calibrationLine.startX + calibrationLine.endX) / 2;
          const midY = (calibrationLine.startY + calibrationLine.endY) / 2;
          const text = isGridSnapActive
            ? `${calibrationLine.percentage}% (${displayAngle}°)`
            : `${calibrationLine.percentage}%`;
          ctx.font = 'bold 16px Arial';
          ctx.fillStyle = 'white';
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 4;
          ctx.strokeText(text, midX, midY - 10);
          ctx.fillText(text, midX, midY - 10);
          ctx.restore();
        }
      }

      if (isElementDetectionMode && hoveredPDFElement) {
        ctx.save();
        let highlightColor = 'rgba(255, 215, 0, 0.9)';
        let fillColor = 'rgba(255, 215, 0, 0.2)';
        if (hoveredPDFElement.type === 'xobject') {
          highlightColor = 'rgba(255, 0, 255, 0.9)';
          fillColor = 'rgba(255, 0, 255, 0.3)';
        } else if (hoveredPDFElement.type === 'text') {
          highlightColor = 'rgba(0, 255, 255, 0.9)';
          fillColor = 'rgba(0, 255, 255, 0.2)';
        }
        ctx.strokeStyle = highlightColor;
        ctx.lineWidth = 5;
        ctx.shadowColor = highlightColor.replace('0.9', '0.6');
        ctx.shadowBlur = 20;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        const { x, y, width, height } = hoveredPDFElement.bounds;
        ctx.fillStyle = fillColor;
        ctx.fillRect(x, y, width, height);
        ctx.strokeRect(x, y, width, height);
        if (hoveredPDFElement.text || hoveredPDFElement.objectName) {
          const label = hoveredPDFElement.text || hoveredPDFElement.objectName || '';
          ctx.font = 'bold 16px Arial';
          ctx.fillStyle = 'white';
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
          ctx.lineWidth = 3;
          ctx.strokeText(label.substring(0, 40), x, y - 10);
          ctx.fillText(label.substring(0, 40), x, y - 10);
        }
        ctx.restore();
      }

      if (showDebugTileGrid && !debugTileGridRef.current && canvas) {
        const pdfCanvas = document.querySelector('.react-pdf__Page__canvas') as HTMLCanvasElement;
        if (pdfCanvas) {
          const aspect = pdfCanvas.height / pdfCanvas.width;
          const hrW = 16000;
          const hrH = Math.round(hrW * aspect);
          const gs = 10;
          const tw = Math.floor(hrW / gs);
          const th = Math.floor(hrH / gs);
          const ov = Math.floor(tw * 0.15);
          (debugTileGridRef as React.MutableRefObject<{
            gridSize: number;
            tileWidth: number;
            tileHeight: number;
            overlap: number;
            hiResWidth: number;
            hiResHeight: number;
          } | null>).current = {
            gridSize: gs,
            tileWidth: tw,
            tileHeight: th,
            overlap: ov,
            hiResWidth: hrW,
            hiResHeight: hrH,
          };
        }
      }
      const gridParams = debugTileGridRef.current;
      if (showDebugTileGrid && gridParams && canvas) {
        const { gridSize: gs, tileWidth: tw, tileHeight: th, overlap: ov, hiResWidth: hrW, hiResHeight: hrH } =
          gridParams;
        const sX = canvas.width / hrW;
        const sY = canvas.height / hrH;
        ctx.save();
        ctx.strokeStyle = 'red';
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        for (let i = 1; i < gs; i++) {
          const vx = Math.round(i * tw * sX);
          ctx.beginPath();
          ctx.moveTo(vx, 0);
          ctx.lineTo(vx, canvas.height);
          ctx.stroke();
          const hy = Math.round(i * th * sY);
          ctx.beginPath();
          ctx.moveTo(0, hy);
          ctx.lineTo(canvas.width, hy);
          ctx.stroke();
        }
        if (ov > 0) {
          ctx.strokeStyle = 'rgba(255, 0, 0, 0.4)';
          ctx.lineWidth = 1;
          ctx.setLineDash([6, 4]);
          const ovX = ov * sX;
          const ovY = ov * sY;
          for (let i = 1; i < gs; i++) {
            const vx = Math.round(i * tw * sX);
            ctx.beginPath();
            ctx.moveTo(vx - ovX, 0);
            ctx.lineTo(vx - ovX, canvas.height);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(vx + ovX, 0);
            ctx.lineTo(vx + ovX, canvas.height);
            ctx.stroke();
            const hy = Math.round(i * th * sY);
            ctx.beginPath();
            ctx.moveTo(0, hy - ovY);
            ctx.lineTo(canvas.width, hy - ovY);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(0, hy + ovY);
            ctx.lineTo(canvas.width, hy + ovY);
            ctx.stroke();
          }
        }
        ctx.setLineDash([]);
        ctx.font = 'bold 14px monospace';
        ctx.fillStyle = 'red';
        ctx.textBaseline = 'top';

        const drawPass = (offsetX: number, offsetY: number, passColor: string, passLabel: string) => {
          const cols = Math.ceil((hrW - offsetX) / tw);
          const rows = Math.ceil((hrH - offsetY) / th);
          for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
              const baseLeft = c * tw + offsetX;
              const baseTop = r * th + offsetY;
              if (baseLeft >= hrW || baseTop >= hrH) continue;
              const lx = Math.round(baseLeft * sX);
              const ly = Math.round(baseTop * sY);
              const bw = Math.round(Math.min(hrW - baseLeft, tw) * sX);
              const bh = Math.round(Math.min(hrH - baseTop, th) * sY);
              ctx.strokeStyle = passColor;
              ctx.lineWidth = passLabel === 'P1' ? 2 : 1;
              if (passLabel === 'P2') ctx.setLineDash([4, 4]);
              else ctx.setLineDash([]);
              ctx.strokeRect(lx, ly, bw, bh);
              if (ov > 0) {
                const rgba = passColor.replace('rgb', 'rgba').replace(')', ', 0.3)');
                ctx.strokeStyle = rgba;
                ctx.lineWidth = 1;
                ctx.setLineDash([2, 2]);
                const cLeft = Math.max(0, baseLeft - ov) * sX;
                const cTop = Math.max(0, baseTop - ov) * sY;
                const cRight = Math.min(hrW, baseLeft + tw + ov) * sX;
                const cBottom = Math.min(hrH, baseTop + th + ov) * sY;
                ctx.strokeRect(cLeft, cTop, cRight - cLeft, cBottom - cTop);
              }
              const label = `${passLabel}:R${r}C${c}`;
              const metrics = ctx.measureText(label);
              ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
              ctx.setLineDash([]);
              ctx.fillRect(lx + 2, ly + 2, metrics.width + 4, 16);
              ctx.fillStyle = passColor;
              ctx.fillText(label, lx + 4, ly + 4);
            }
          }
        };
        drawPass(0, 0, 'rgb(255, 0, 0)', 'P1');
        if (ov > 0) {
          drawPass(Math.floor(tw / 2), Math.floor(th / 2), 'rgb(0, 0, 255)', 'P2');
        }
        ctx.restore();
      }
    },
    [
      annotations,
      pageNumber,
      selectedAnnotation,
      isDrawing,
      currentAnnotation,
      dragStart,
      resizeHandle,
      currentColor,
      thickness,
      highlightedComponentType,
      viewAllMode,
      animatingAnnotations,
      selectionRect,
      isSelecting,
      selectedAnnotations,
      snapshotSelection,
      showSnapshotActions,
      calibrationLine,
      isCalibrating,
      isGridSnapActive,
      calibrationData,
      getColorForAnnotation,
      getAnimationScale,
      pipingPreview,
      pipingSnapTarget,
      currentTool,
      calculatePipeMeasurement,
      isElementDetectionMode,
      hoveredPDFElement,
      autocountSelection,
      autocountMode,
      keyExtractionSelection,
      keyExtractionMode,
      lightingScheduleSelection,
      lightingScheduleMode,
      showDebugTileGrid,
      rescaleAnnotation,
      wireRunExtendMode,
      wireRunExtendPreviewPoint,
      hoveredAnnotation,
      hoveredAnnotationPoint,
    ]
  );

  return { drawAnnotations };
}
